/**
 * Claims Dashboard
 * Shows metrics, trends, and analytics for claims management
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDashboardMetrics, getDashboardTrends, formatCurrency, getPhase1DashboardMetrics } from '../api/claims';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const ClaimsDashboard = () => {
  const navigate = useNavigate();

  // Fetch metrics
  const { data: metricsData, isLoading: metricsLoading } = useQuery({
    queryKey: ['claimMetrics'],
    queryFn: getDashboardMetrics
  });

  // Fetch trends
  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ['claimTrends'],
    queryFn: () => getDashboardTrends(12)
  });

  // Fetch Phase 1 metrics
  const { data: phase1Data, isLoading: phase1Loading } = useQuery({
    queryKey: ['claimPhase1Metrics'],
    queryFn: getPhase1DashboardMetrics
  });

  const metrics = metricsData?.metrics || {};
  const trends = trendsData?.trends || {};
  const phase1Metrics = phase1Data || {};

  // Colors for charts
  const STATUS_COLORS = {
    draft: '#9CA3AF',
    submitted: '#3B82F6',
    under_review: '#F59E0B',
    approved: '#10B981',
    in_negotiation: '#8B5CF6',
    settled: '#059669',
    rejected: '#EF4444',
    closed: '#6B7280'
  };

  const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  // Format status breakdown for pie chart
  const statusChartData = Object.entries(metrics.status_breakdown || {}).map(([status, data]) => ({
    name: status.replace('_', ' '),
    value: data.count,
    amount: data.total_value_cents
  }));

  // Format OEM data for bar chart
  const oemChartData = (metrics.by_oem || [])
    .sort((a, b) => b.total_value_cents - a.total_value_cents)
    .slice(0, 10)
    .map(oem => ({
      name: oem.oem,
      claims: oem.count,
      value: oem.total_value_cents / 100
    }));

  // Format trends data for line chart
  const trendsChartData = (trends.created || []).map((item, index) => {
    const settled = trends.settled?.find(s => s.month === item.month && s.year === item.year) || {};
    return {
      month: item.month_name,
      created: item.total_value_cents / 100,
      settled: settled.total_settled_cents / 100 || 0
    };
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Claims Dashboard</h1>
              <p className="mt-1 text-sm text-gray-600">
                Analytics and metrics for claims management
              </p>
            </div>
            <button
              onClick={() => navigate('/claims')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              View All Claims
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {metricsLoading || trendsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Open Claims Value */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Open Claims Value</h3>
                  <span className="text-2xl">💰</span>
                </div>
                <div className="text-3xl font-bold text-red-600">
                  {formatCurrency(metrics.open_claim_value_cents || 0)}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {metrics.total_open_claims || 0} open claims
                </p>
              </div>

              {/* Total Settled Value */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Settled Value</h3>
                  <span className="text-2xl">✅</span>
                </div>
                <div className="text-3xl font-bold text-green-600">
                  {formatCurrency(metrics.settled_value_cents || 0)}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {metrics.status_breakdown?.settled?.count || 0} settled claims
                </p>
              </div>

              {/* Recovery Rate */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Recovery Rate</h3>
                  <span className="text-2xl">📊</span>
                </div>
                <div className="text-3xl font-bold text-blue-600">
                  {metrics.open_claim_value_cents > 0
                    ? ((metrics.settled_value_cents / (metrics.open_claim_value_cents + metrics.settled_value_cents)) * 100).toFixed(1)
                    : 0}%
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Of total claim value
                </p>
              </div>

              {/* In Negotiation */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">In Negotiation</h3>
                  <span className="text-2xl">🤝</span>
                </div>
                <div className="text-3xl font-bold text-purple-600">
                  {metrics.status_breakdown?.in_negotiation?.count || 0}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Active negotiations
                </p>
              </div>
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Status Breakdown Pie Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Claims by Status</h2>
                {statusChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value} claims`} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </div>

              {/* OEM Bar Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Claims by OEM</h2>
                {oemChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={oemChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip
                        formatter={(value, name) => [
                          name === 'value' ? `$${value.toLocaleString()}` : value,
                          name === 'value' ? 'Claim Value' : 'Claims'
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="claims" fill="#3B82F6" name="Claims Count" />
                      <Bar dataKey="value" fill="#10B981" name="Total Value ($)" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </div>
            </div>

            {/* Trends Line Chart */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Month-over-Month Trends</h2>
              {trendsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={trendsChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="created"
                      stroke="#EF4444"
                      strokeWidth={2}
                      name="Claims Created ($)"
                      dot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="settled"
                      stroke="#10B981"
                      strokeWidth={2}
                      name="Claims Settled ($)"
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-96 text-gray-500">
                  No trend data available
                </div>
              )}
            </div>

            {/* Phase 1 Charts */}
            {!phase1Loading && phase1Metrics && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Claims by Probability */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Claims by Probability</h2>
                  {phase1Metrics.by_probability && Object.keys(phase1Metrics.by_probability).length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={Object.entries(phase1Metrics.by_probability).map(([prob, data]) => ({
                            name: prob.charAt(0).toUpperCase() + prob.slice(1),
                            value: data.count,
                            fill: prob === 'low' ? '#10B981' : prob === 'medium' ? '#F59E0B' : '#EF4444'
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={80}
                          dataKey="value"
                        >
                          {Object.entries(phase1Metrics.by_probability).map(([prob], index) => (
                            <Cell key={`cell-${index}`} fill={prob === 'low' ? '#10B981' : prob === 'medium' ? '#F59E0B' : '#EF4444'} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No probability data available</p>
                  )}
                </div>

                {/* Claims by Stage (Funnel) */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Claims by Workflow Stage</h2>
                  {phase1Metrics.by_stage && phase1Metrics.by_stage.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={phase1Metrics.by_stage.map(s => ({ ...s, stage_name: s.stage.replace('_', ' ') }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="stage_name" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3B82F6" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No stage data available</p>
                  )}
                </div>
              </div>
            )}

            {/* Top Claims Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Top 10 Highest Value Claims</h2>
              </div>
              <div className="overflow-x-auto">
                {metrics.top_claims && metrics.top_claims.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Claim #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Opportunity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          OEM
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Part
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Value
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {metrics.top_claims.map((claim) => (
                        <tr key={claim.claim_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            {claim.claim_number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {claim.opportunity_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {claim.oem}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {claim.part_number || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {claim.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                            {formatCurrency(claim.financial_impact_cents)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => navigate(`/claims/${claim.claim_id}`)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No claims data available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimsDashboard;
